import Information16 from '@carbon/icons-react/es/information/16';
import { Editor } from '@ckeditor/ckeditor5-core/src/editor';
import React, { FC, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { IconShell } from 'components/atoms/icon-shell';
import { FilterBar } from 'components/molecules/filter-bar';
import { EditorsObject } from 'components/organisms/ck-collab-editor';
import {
  saveAllEditors,
  setActiveEditor,
  useDocuments,
} from 'components/organisms/document/document-store';
import { Tools } from 'components/organisms/document/parts/topbar/types.d';
import { Text03 } from 'components/styles';
import { EditorModes } from 'data/documents';
import { useStatusField } from 'data/fields/hooks';
import { Task } from 'data/tasks';
import {
  createDocumentTemplatePrivateSectionAction,
  createDocumentTemplateHighlightTaskAction,
  updateDocumentTemplateAction,
} from 'data/templates/documents';
import { useDocumentById } from 'data/templates/documents/hooks/use-document-by-id';
import { useTemplateMentionsData } from 'data/templates/hooks';
import { ProjectTemplateTask } from 'data/templates/projects';
import { floatUid } from 'utils/uid';
import {
  TemplateEditorProps,
  TemplateRocketlaneDocumentEditor,
} from './editor';
import { TaskPaneTab } from './task-pane-tab';
import { TemplateDocumentEditorToolbar } from './toolbar';

const EditorWrapper = styled.div`
  display: flex;
  height: calc(100% - 52px);
`;
const EditorContainer = styled.div`
  flex: 1;
  overflow: auto;
`;

const ReadonlyToolbar = styled(FilterBar)`
  ${Text03};
`;

export const TemplateDocumentsPane: FC<{
  docId: number;
  editorProps?: TemplateEditorProps;
  onTitleChange(title: string): void;
  tools?: Tools;
}> = ({ docId, editorProps, onTitleChange, tools }) => {
  const dispatch = useDispatch();
  const [state, documentStoreDispatch] = useDocuments();
  const document = useDocumentById(docId, true);
  const statusField = useStatusField();
  const { members } = useTemplateMentionsData();

  const [privateSectionEnabled, setPrivateSectionStatus] = useState(true);

  const onInitEditorInstance = (editors: EditorsObject) => {
    if (editors[EditorModes.Public]) {
      setActiveEditor(documentStoreDispatch, editors[EditorModes.Public]);
    }
    saveAllEditors(documentStoreDispatch, editors);
  };

  const onEditorFocusChange = (editor: Editor) => {
    setActiveEditor(documentStoreDispatch, editor);
  };

  const onTogglePrivateSection = (status: boolean) => {
    dispatch(createDocumentTemplatePrivateSectionAction({ docId }));

    setPrivateSectionStatus(status);

    dispatch(
      updateDocumentTemplateAction({
        template: {
          templateId: docId,
        },
      }),
    );
  };

  const handleAddNewHighlightTask = (task: Partial<Task>): number => {
    const sectionId = parseInt(
      state.editor.sourceElement.dataset.sectionid,
      10,
    );
    const _task = {
      ...task,
      private: sectionId === 0,
      taskId: floatUid(),
    };

    const resourceSectionId = floatUid();

    dispatch(
      createDocumentTemplateHighlightTaskAction({
        docId,
        sectionId,
        resourceSectionId,
        task: {
          ..._task,
          fields: [
            {
              fieldId: statusField.fieldId,
              fieldValue: statusField.metaData?.default as number,
            },
          ],
        } as unknown as ProjectTemplateTask,
      }),
    );

    return resourceSectionId;
  };

  return (
    <>
      {editorProps?.readOnly ? (
        <ReadonlyToolbar>
          <FilterBar.Primary>
            <IconShell icon={<Information16 />} size="small" />
            <FormattedMessage id="templates.documents.toolbar.readonly" />
          </FilterBar.Primary>
        </ReadonlyToolbar>
      ) : (
        <TemplateDocumentEditorToolbar
          activeEditor={state.editor}
          editors={state.allEditors}
          isPrivateSectionEnabled={privateSectionEnabled}
          readOnly={editorProps?.readOnly}
          title=""
          onTogglePrivateSection={onTogglePrivateSection}
          tools={tools}
        />
      )}
      {document && (
        <EditorWrapper>
          <EditorContainer>
            <TemplateRocketlaneDocumentEditor
              activeEditor={state.editor}
              assignee={members.items}
              docId={docId}
              editorProps={editorProps}
              privateSectionEnabled={privateSectionEnabled}
              onInitEditorInstance={onInitEditorInstance}
              onEditorFocusChange={onEditorFocusChange}
              onTogglePrivateSection={setPrivateSectionStatus}
              onUpdateDocumentTitle={onTitleChange}
              onAddHighlightTask={handleAddNewHighlightTask}
            />
          </EditorContainer>
          <TaskPaneTab docId={docId} readOnly={editorProps?.readOnly} />
        </EditorWrapper>
      )}
    </>
  );
};
